import React from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Helmet } from "react-helmet";
import { courses } from "../data/Courses";
import { courseDescriptions } from "../data/courseDescriptions";

const CourseDetail = () => {
  const { courseName } = useParams();
  const navigate = useNavigate();

  // Find the course from the courses data
  const course = courses.find(c => c.originalName.replace(".html", "") === courseName);

  // If course not found, show placeholder data
  const courseData = course || {
    displayName: "CompTIA Cybersecurity Analyst (CSA) Course",
    imageUrl: "/images/logocysaplus-01.svg",
    categories: ["CompTIA", "Cybersecurity"]
  };

  // Look up course-specific content; fall back to CySA+ defaults
  const descriptionData = courseDescriptions[courseName] || null;

  const courseDescription = descriptionData
    ? descriptionData.description
    : "The CompTIA Cybersecurity Analyst (CSA) certification is designed for individuals looking to establish a career in cybersecurity analysis. This course provides a foundational understanding of cybersecurity principles, focusing on the skills needed to analyze and respond to security threats. Participants will learn about threat detection, incident response, and the tools and techniques used to protect organizational assets.";

  const entryRequirementsText = descriptionData
    ? descriptionData.entryRequirements
    : "To enroll in the CompTIA Cybersecurity Analyst (CySA+) course, candidates should possess a foundational understanding of IT concepts and cybersecurity principles. It is highly recommended that applicants have a CompTIA Security+ certification or equivalent knowledge. Additionally, candidates should ideally have 3 to 4 years of hands-on experience in IT security or a related field to ensure they can fully engage with the course material. This background will help students effectively grasp the advanced topics covered in the course and prepare them for the challenges of a cybersecurity analyst role.";

  // Key learning objectives
  const learningObjectives = descriptionData
    ? descriptionData.learningObjectives
    : [
        "Understand the role and responsibilities of a cybersecurity analyst.",
        "Gain insights into threat intelligence and vulnerability management.",
        "Learn to analyze security incidents and respond effectively.",
        "Familiarize yourself with security tools and technologies used in the industry.",
        "Prepare for the CSA certification exam with practical exercises and case studies.",
      ];

  // Program requirements data
  const programRequirements = descriptionData
    ? descriptionData.programRequirements
    : [
        { label: "Qualification Level:", value: "Professional Certification" },
        { label: "Credits:", value: "30 (equivalent to the course workload)" },
        { label: "Duration:", value: "3 to 6 months (depending on study pace)" },
        { label: "Study Options:", value: "Full-time or Part-time" },
        {
          label: "Prerequisites:",
          value:
            "Recommended: CompTIA Security+ or equivalent knowledge; 3-4 years of hands-on experience in IT security is beneficial.",
        },
      ];

  return (
    <div className="bg-white flex flex-row justify-center w-full p-6 md:px-32">
      <Helmet>
        <title>{courseData.displayName} | Ikusasa Technology Solutions</title>
        <meta
          name="description"
          content={`Learn about the ${courseData.displayName} course offered by Ikusasa Technology Solutions. Gain professional skills and certification.`}
        />
      </Helmet>
      
      <div className="bg-white w-full max-w-[1440px] relative min-h-[1200px]">
        {/* Main content */}
        <div className="mt-8 flex flex-col lg:flex-row gap-8">
          {/* Course logo */}
          <div className="flex-shrink-0">
            <img
              className="w-48 h-48 object-cover"
              alt={`${courseData.displayName} Logo`}
              src={courseData.imageUrl}
            />
          </div>

          {/* Course title */}
          <div className="flex-grow">
            <h1 className="font-bold text-black text-3xl leading-tight">
              {courseData.displayName}
            </h1>
            <div className="flex flex-wrap gap-2 mt-4">
              {courseData.categories.map((category, index) => (
                <span 
                  key={index} 
                  className="bg-[#016188] text-white px-3 py-1 rounded-full text-sm"
                >
                  {category}
                </span>
              ))}
            </div>
          </div>
        </div>

        {/* Course content sections */}
        <div className="mt-16 grid grid-cols-1 lg:grid-cols-3 gap-8">
          <div className="lg:col-span-2">
            {/* Course Description */}
            <section className="mb-16">
              <h2 className="font-bold text-black text-2xl md:text-3xl mb-4">
                Course Description
              </h2>
              <div className="w-24 h-[7px] bg-[#930a0d] mb-8"></div>
              <p className="font-normal text-black text-base">
                {courseDescription}
              </p>
            </section>

            {/* Key Learning Objectives */}
            <section className="mb-16">
              <h2 className="font-bold text-black text-2xl md:text-3xl mb-4">
                Key Learning Objectives
              </h2>
              <div className="w-24 h-[7px] bg-[#930a0d] mb-8"></div>
              <ul className="font-normal text-black text-base list-disc pl-6 space-y-2">
                {learningObjectives.map((objective, index) => (
                  <li key={index}>{objective}</li>
                ))}
              </ul>

              {/* Action buttons */}
              <div className="flex gap-4 mt-12">
                <button
                  onClick={() => navigate(`/apply/${courseName}`)}
                  className="bg-[#930a0d] text-white rounded-[20px] h-12 w-[195px] font-bold"
                >
                  Apply
                </button>
                <button 
                  onClick={() => navigate("/contact")}
                  className="bg-[#930a0d] text-white rounded-[20px] h-12 w-[195px] font-bold"
                >
                  Enquire
                </button>
              </div>
            </section>

            {/* Entry Requirements */}
            <section className="mb-16">
              <h2 className="font-bold text-black text-2xl md:text-3xl mb-4">
                Entry Requirements
              </h2>
              <div className="w-24 h-[7px] bg-[#930a0d] mb-8"></div>
              <p className="font-normal text-black text-base">
                {entryRequirementsText}
              </p>
            </section>
          </div>

          {/* Right sidebar */}
          <div className="lg:col-span-1">
            {/* Program Requirements Card */}
            <div className="bg-[#930a0d] rounded-xl text-white mb-8 p-6">
              <div className="mb-4">
                <h3 className="text-2xl md:text-3xl text-center font-bold">
                  Program Requirements
                </h3>
              </div>
              <div>
                <dl className="space-y-4 text-base">
                  {programRequirements.map((item, index) => (
                    <div key={index} className="mb-4">
                      <dt className="font-bold inline">{item.label}</dt>
                      <dd className="inline ml-1">{item.value}</dd>
                    </div>
                  ))}
                </dl>
              </div>
            </div>

            {/* Available Dates Card */}
            <div className="bg-[#016188] text-white p-5 rounded-xl">
              <p className="text-lg">
                <span className="font-extrabold">Available Dates:</span> 5 May
                - 5 October
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CourseDetail;
